import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal,
} from "@/lib/components/Dialog";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import {
  useCanOpenCreditsDialog,
  useCoupon,
  useSyncBillingAfterPayment,
} from "./billing-hooks";
import { useEffect, useRef, useState } from "react";
import { AnalyticsApi } from "@/lib/api/analytics-api";
import { IconCheck } from "@tabler/icons-react";
import PlanTiersTab from "./PlanTiersTab";
import { PlanType } from "./PlanTier";
import PillToggleGroup from "@/lib/components/PillToggleGroup";
import { TeamDto } from "@repo/api/team";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";

type Props = {
  teamExternalId: string;
  redirectVideoExternalId?: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  missingCredits?: number;
  numSelectedImages?: number;
  onCreateCheckoutSuccess?: () => void;
  team: TeamDto;
};

export default function AddCreditsDialog({
  teamExternalId,
  isOpen,
  setIsOpen,
  missingCredits,
  numSelectedImages,
  team,
  onCreateCheckoutSuccess,
  redirectVideoExternalId,
}: Props) {
  const initialTab = team.featureFlags.creditDialogInitialTab ?? "SUBSCRIPTION";
  const [activeTab, setActiveTab] = useState(initialTab);
  const { autoApplyCoupon, currentCoupon, setCurrentCoupon } = useCoupon(team);

  const isSyncingBilling = useSyncBillingAfterPayment(teamExternalId);
  const canOpenCreditsDialog = useCanOpenCreditsDialog(
    isOpen,
    setIsOpen,
    teamExternalId,
  );

  // Log analytics events when dialog opens
  const previousIsOpen = useRef(false);
  useEffect(() => {
    if (
      isOpen &&
      !previousIsOpen.current &&
      numSelectedImages &&
      missingCredits
    ) {
      AnalyticsApi.logCreditsPopupOpenedInsufficient(
        missingCredits,
        numSelectedImages,
        initialTab,
        teamExternalId,
      );
    } else if (isOpen && !previousIsOpen.current && !numSelectedImages) {
      AnalyticsApi.logCreditsPopupOpenedManual(initialTab, teamExternalId);
    }

    previousIsOpen.current = isOpen;
  }, [isOpen, numSelectedImages, missingCredits, teamExternalId, initialTab]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  if (isSyncingBilling) {
    return (
      <Dialog open={isSyncingBilling}>
        <DialogPortal>
          <DialogOverlay className="bg-black/50" />
          <div className="fixed top-[50%] left-[50%] z-50 translate-x-[-50%] translate-y-[-50%]">
            <LoadingSpinner className="h-10 w-10 text-white" />
          </div>
        </DialogPortal>
      </Dialog>
    );
  }

  if (!canOpenCreditsDialog || !team) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent
        className="max-h-[min(45rem,100%)] w-full max-w-240"
        classNameInner="flex min-h-0 flex-col gap-0 overflow-hidden p-0 sm:px-0"
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <div className="mt-8 flex w-full items-center justify-center">
            <PillToggleGroup
              items={[
                { label: "One-time", value: "ONE_OFF" },
                {
                  label: "Monthly",
                  value: "SUBSCRIPTION",
                  accentLabel: "18-50% more credits",
                },
              ]}
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as PlanType)}
            />
          </div>

          <div className="flex w-full flex-col">
            <PlanTiersTab
              key={activeTab}
              type={activeTab}
              missingCredits={missingCredits}
              activeSubscription={team.billingInfo?.activeSubscription ?? null}
              onCreateCheckoutSuccess={onCreateCheckoutSuccess}
              teamExternalId={teamExternalId}
              redirectVideoExternalId={redirectVideoExternalId}
              autoApplyCoupon={autoApplyCoupon}
              currentCoupon={currentCoupon}
              setCurrentCoupon={setCurrentCoupon}
              currentCredits={team.billingInfo?.creditBalance ?? 0}
            />
          </div>
        </div>
        <div className="flex w-full shrink-0 items-center justify-between gap-4 border-t border-border px-4 py-6 md:px-8">
          <div className="flex-1">
            <p className="mb-0.5 flex items-center text-sm">
              <IconCheck
                size={16}
                className="mr-1.5 shrink-0 grow-0 basis-auto"
              />
              <span>Includes&nbsp;</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-text-primary underline decoration-dashed decoration-0 underline-offset-3">
                    {activeTab === "SUBSCRIPTION" ? (
                      <span className="font-semibold">
                        10 free re-animations/image
                      </span>
                    ) : (
                      "1 free re-animation/image"
                    )}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Custom animations made in Creative Mode are excluded from
                    the free re-animations.
                  </p>
                </TooltipContent>
              </Tooltip>
            </p>

            <p className="mb-0.5 flex items-center gap-1.5 text-sm">
              <IconCheck size={16} className="shrink-0 grow-0 basis-auto" />
              <span className="text-text-primary">
                Credits are valid for 12 months
                {activeTab === "SUBSCRIPTION" && " from the date added"}
              </span>
            </p>
          </div>

          <img
            src="/powered-by-stripe.svg"
            alt="Powered by Stripe"
            className="h-7"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
